import { Dispatch, SetStateAction } from 'react'

import { Box, Flex, Heading, Switch, Text, VStack } from '@chakra-ui/react'
import useTranslation from 'next-translate/useTranslation'

import { EzTendaEmailAddress } from 'constants/email'
import { SubscriptionPlans, YEARLY_DISCOUNT } from 'constants/subscription'
import {
  Region,
  SubscriptionQuery,
  SubscriptionTier,
  UserType,
} from 'generated/generated-graphql'
import { useRegion } from 'providers/RegionProvider'
import {
  getStripeUserType,
  StripeUserType,
} from 'routes/Profile/SubscriptionOverview/BrandSubscriptionOverview'

import { SubscriptionCard } from './SubscriptionCard'
import { SubscriptionCardSpain } from './SubscriptionCardSpain'

interface ChoosePlanSelectionProps {
  userType: UserType
  subscriptionData?: SubscriptionQuery['subscription']
  isEnterpriseSubscriptionRequestSent?: boolean
  onSubscriptionPlanSelectionClick: (tier: SubscriptionTier) => void
  isYearlyPeriod: boolean
  setIsYearlyPeriod: Dispatch<SetStateAction<boolean>>
  onSkipClick?: () => void
}

const SUBSCRIPTION_CARD_REGION_MAP = {
  [Region.Australia]: SubscriptionCard,
  [Region.UnitedKingdom]: SubscriptionCard,
  [Region.Spain]: SubscriptionCardSpain,
}

export const ChoosePlanSelection = ({
  userType,
  subscriptionData,
  isEnterpriseSubscriptionRequestSent: _isEnterpriseSubscriptionRequestSent,
  isYearlyPeriod,
  setIsYearlyPeriod,
  onSubscriptionPlanSelectionClick,
  onSkipClick,
}: ChoosePlanSelectionProps) => {
  const { t } = useTranslation('common')
  const { region } = useRegion()
  const subscriptionUserType = getStripeUserType(subscriptionData)
  const isUserActive = subscriptionUserType === StripeUserType.Active
  const currentPlan = SubscriptionPlans[region].find(
    (sub) => sub.subscriptionTier === subscriptionData?.subscriptionTier
  )

  const isEnterpriseSubscriptionRequestSent =
    region !== Region.Australia && _isEnterpriseSubscriptionRequestSent

  const emailAddress = EzTendaEmailAddress[region]
  const SubscriptionCardComponent = SUBSCRIPTION_CARD_REGION_MAP[region]

  if (userType !== UserType.Brand) {
    return null
  }

  return (
    <Flex
      textAlign="center"
      justifyContent="center"
      direction="column"
      wrap="wrap"
      my="32px"
      gap="32px"
    >
      {region === Region.Australia && (
        <Flex
          gap="12px"
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <Flex gap="14px" justifyContent="center">
            <Text fontSize="20px">{t('Subscriptions.monthly')}</Text>
            <Switch
              isChecked={isYearlyPeriod}
              onChange={() => setIsYearlyPeriod(!isYearlyPeriod)}
            />
            <Text fontSize="20px">{t('Subscriptions.yearly')}</Text>
          </Flex>
          {YEARLY_DISCOUNT > 0 && (
            <Text fontSize="16px" color="brand.darkBlueGray">
              {t('Subscriptions.saveYearly', {
                value: YEARLY_DISCOUNT,
              })}
            </Text>
          )}
        </Flex>
      )}
      <Flex
        w="full"
        flexDirection={{ base: 'column', lg: 'row' }}
        gridGap="48px"
        animation="fadeInLeft 1200ms ease-out"
      >
        {SubscriptionPlans[region]
          .filter(({ isLegacy, price }) => !isLegacy && price > 0)
          .map((sub) => (
            <SubscriptionCardComponent
              key={sub.subscriptionTier}
              title={t(sub.name)}
              tier={sub.subscriptionTier}
              caption={sub.caption ? t(sub.caption) : undefined}
              bulletPoints={sub.bulletPoints}
              isYearlyPeriod={Boolean(sub.yearlyPrice) && isYearlyPeriod}
              price={
                isYearlyPeriod ? (sub.yearlyPrice ?? sub.price) : sub.price
              }
              currentPlan={isUserActive ? currentPlan : undefined}
              isDisabled={isEnterpriseSubscriptionRequestSent}
              buttonTooltipText={
                isEnterpriseSubscriptionRequestSent
                  ? t('TRANSLATION_432')
                  : undefined
              }
              onSubscriptionPlanClick={onSubscriptionPlanSelectionClick}
            />
          ))}
      </Flex>
      <Box h="20px" fontSize="18px" color="brand.wildBlueYonder">
        {onSkipClick && (
          <Text
            as="button"
            fontFamily="proxima Nova Semibold"
            onClick={onSkipClick}
          >
            {t('Subscriptions.skip')} &#10230;
          </Text>
        )}
      </Box>

      <VStack spacing="12px">
        <Heading
          fontFamily="Cocogoose Pro"
          mt="24px"
          fontSize="3xl"
          lineHeight="9"
          color="brand.ultramarine"
          mb={4}
        >
          {t('BRAND_PROFILE_SUBSCRIPTION_QUESTIONS')}
        </Heading>
        <Text fontSize="16px">
          {
            // @ts-ignore
            t('TRANSLATION_433', {
              emailAddress: emailAddress || '',
            })
          }
        </Text>
      </VStack>
    </Flex>
  )
}
